/**
 * NotificationsTab.tsx
 *
 * My Preferences → Notifications tab.
 * Collapsible sections with Select All / Deselect All controls.
 * Locked items (🔒) are always on, shown with a lock icon and brief explanation.
 * Permission-filtered: sections irrelevant to the current user's permissions are hidden.
 * Email toggle is hidden for R1.
 */

import { ChevronDown, Lock } from "lucide-react";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { usePermissions } from "@/hooks/usePermissions";
import { useTerminology } from "@/hooks/useTerminology";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface NotificationItem {
  id: string;
  label: string;
  locked: boolean;
  lockedReason?: string;
}

interface NotificationSection {
  id: string;
  title: string;
  /** If any of these permissions are present, the section is shown. Omit to always show. */
  requiredAnyPermission?: string[];
  items: NotificationItem[];
}

// ─── Notification catalogue ───────────────────────────────────────────────────

interface TermFns {
  forecast: (capitalize?: boolean) => string;
  scenario: (capitalize?: boolean, plural?: boolean) => string;
  actions: (capitalize?: boolean, plural?: boolean) => string;
}

function buildNotificationSections({
  forecast,
  scenario,
  actions,
}: TermFns): NotificationSection[] {
  return [
    {
      id: "data-sources",
      title: "Data Sources",
      requiredAnyPermission: ["configure_data_sources"],
      items: [
        { id: "DS-01", label: "New data source connected", locked: false },
        {
          id: "DS-02",
          label: "Data source disconnected",
          locked: true,
          lockedReason: "Required to maintain data quality awareness",
        },
        { id: "DS-03", label: "Data source sync completed", locked: false },
        { id: "DS-04", label: "Data source sync failed", locked: false },
        {
          id: "DS-05",
          label: "Data source auth token expired",
          locked: true,
          lockedReason: "Required to prevent undetected sync failures",
        },
        {
          id: "DS-06",
          label: "Data source auth token expiring soon",
          locked: true,
          lockedReason: "Required to allow time to renew before expiry",
        },
        {
          id: "DS-07",
          label: "Data source configuration changed",
          locked: false,
        },
        { id: "DS-08", label: "New transactions imported", locked: false },
        {
          id: "DS-09",
          label: "Transaction mapping rule triggered",
          locked: false,
        },
        {
          id: "DS-10",
          label: "Data source returned incomplete data",
          locked: true,
          lockedReason: `Required to ensure ${forecast(false)} accuracy issues are not missed`,
        },
      ],
    },
    {
      id: "scenarios",
      title: scenario(true),
      requiredAnyPermission: [
        "view_scenarios",
        "create_scenarios",
        "approve_reject_scenarios",
      ],
      items: [
        {
          id: "SC-01",
          label: `${scenario(true, false)} submitted for review`,
          locked: true,
          lockedReason: "Required for workflow participation",
        },
        {
          id: "SC-02",
          label: `${scenario(true, false)} reviewed`,
          locked: true,
          lockedReason: "Required for workflow participation",
        },
        {
          id: "SC-03",
          label: `${scenario(true, false)} approved`,
          locked: true,
          lockedReason: "Required for workflow participation",
        },
        {
          id: "SC-04",
          label: `${scenario(true, false)} committed`,
          locked: true,
          lockedReason: "Required for workflow participation",
        },
        {
          id: "SC-05",
          label: `${scenario(true, false)} activated`,
          locked: true,
          lockedReason: "Required for workflow participation",
        },
        {
          id: "SC-06",
          label: `${scenario(true, false)} parked`,
          locked: true,
          lockedReason: "Required for workflow participation",
        },
        {
          id: "SC-07",
          label: `${scenario(true, false)} archived`,
          locked: true,
          lockedReason: "Required for workflow participation",
        },
      ],
    },
    {
      id: "forecasting",
      title: forecast(true),
      requiredAnyPermission: ["view_forecasts", "trigger_forecast_refresh"],
      items: [
        {
          id: "FC-01",
          label: "Scheduled refresh started",
          locked: true,
          lockedReason: "System event — required for audit awareness",
        },
        {
          id: "FC-02",
          label: "Scheduled refresh completed",
          locked: true,
          lockedReason: `System event — required for ${forecast(false)} currency awareness`,
        },
        { id: "FC-03", label: "Scheduled refresh failed", locked: false },
        {
          id: "FC-04",
          label: "Scheduled refresh missed",
          locked: true,
          lockedReason: "Required to detect scheduling issues",
        },
        { id: "FC-05", label: "Recurring commitment due", locked: false },
        { id: "FC-06", label: "Recurring commitment overdue", locked: false },
        {
          id: "FC-07",
          label: "Recurring commitment ending soon",
          locked: false,
        },
        {
          id: "FC-08",
          label: `${forecast(true)} refresh started (manual)`,
          locked: false,
        },
        {
          id: "FC-09",
          label: `${forecast(true)} refresh completed (manual)`,
          locked: true,
          lockedReason: `Required for ${forecast(false)} currency awareness`,
        },
        {
          id: "FC-10",
          label: `${scenario(true, false)} impact values recalculated`,
          locked: false,
        },
      ],
    },
    {
      id: "actions",
      title: actions(true),
      requiredAnyPermission: ["create_actions", "complete_actions"],
      items: [
        {
          id: "AC-01",
          label: `${actions(true, false)} assigned to you`,
          locked: true,
          lockedReason: "Required so you never miss an assignment",
        },
        {
          id: "AC-02",
          label: `${actions(true, false)} due soon`,
          locked: false,
        },
        {
          id: "AC-03",
          label: `${actions(true, false)} overdue`,
          locked: true,
          lockedReason: "Required to prevent missed deadlines",
        },
        {
          id: "AC-04",
          label: `${actions(true, false)} completed (assigned by you)`,
          locked: true,
          lockedReason: "Required to track completion of delegated tasks",
        },
      ],
    },
    {
      id: "recurring-transactions",
      title: "Recurring Commitments",
      requiredAnyPermission: ["manage_recurring_transactions"],
      items: [
        {
          id: "RT-01",
          label: "Recurring commitment added",
          locked: true,
          lockedReason: `Required to maintain awareness of Base ${forecast(true)} changes`,
        },
        {
          id: "RT-02",
          label: "Recurring commitment modified",
          locked: true,
          lockedReason: `Required to maintain awareness of Base ${forecast(true)} changes`,
        },
      ],
    },
    {
      id: "reports",
      title: "Reports",
      requiredAnyPermission: [
        "view_reports",
        "manage_custom_reports",
        "manage_report_permissions",
      ],
      items: [
        { id: "RP-01", label: "Scheduled report generated", locked: false },
        {
          id: "RP-02",
          label: "Scheduled report failed",
          locked: true,
          lockedReason: "Required to ensure report failures are not missed",
        },
        { id: "RP-03", label: "Report shared with you", locked: false },
        {
          id: "RP-04",
          label: "Report export ready",
          locked: true,
          lockedReason: "Required to confirm download availability",
        },
      ],
    },
    {
      id: "ai-recommendations",
      title: "AI Recommendations",
      requiredAnyPermission: [
        "view_operational_insights",
        "view_strategic_insights",
      ],
      items: [
        { id: "AI-01", label: "New recommendation available", locked: false },
      ],
    },
    {
      id: "users",
      title: "Users",
      requiredAnyPermission: ["manage_users"],
      items: [
        {
          id: "US-01",
          label: "New user created",
          locked: true,
          lockedReason: "Required for access control awareness",
        },
        {
          id: "US-02",
          label: "User deactivated",
          locked: true,
          lockedReason: "Required for access control awareness",
        },
        {
          id: "US-03",
          label: "User role changed",
          locked: true,
          lockedReason: "Required for access control awareness",
        },
        {
          id: "US-04",
          label: "User permissions changed",
          locked: true,
          lockedReason: "Required for access control awareness",
        },
        { id: "US-05", label: "User logged in from new device", locked: false },
      ],
    },
    {
      id: "roles-permissions",
      title: "Roles & Permissions",
      requiredAnyPermission: ["manage_roles_permissions"],
      items: [
        { id: "RPER-01", label: "Role created", locked: false },
        {
          id: "RPER-02",
          label: "Role modified",
          locked: true,
          lockedReason: "Required for governance awareness",
        },
        { id: "RPER-03", label: "Role deleted", locked: false },
        {
          id: "RPER-04",
          label: "Permissions template changed",
          locked: true,
          lockedReason: "Required for governance awareness",
        },
        { id: "RPER-05", label: "User assigned to role", locked: false },
        { id: "RPER-06", label: "User removed from role", locked: false },
      ],
    },
    {
      id: "user-defaults",
      title: "User Defaults",
      requiredAnyPermission: ["manage_user_defaults"],
      items: [
        { id: "UD-01", label: "Default settings changed", locked: false },
      ],
    },
    {
      id: "category-mapping",
      title: "Parent Child Categories",
      requiredAnyPermission: ["configure_data_sources"],
      items: [
        {
          id: "CM-01",
          label: "Child category rule changed",
          locked: true,
          lockedReason:
            "Required to maintain awareness of categorisation rule changes",
        },
      ],
    },
    {
      id: "general-settings",
      title: "General Settings",
      requiredAnyPermission: ["manage_general_settings"],
      items: [
        { id: "GS-01", label: "General settings changed", locked: false },
      ],
    },
    {
      id: "terminology",
      title: "Terminology",
      requiredAnyPermission: ["manage_terminology"],
      items: [
        {
          id: "TM-01",
          label: "Terminology configuration changed",
          locked: false,
        },
      ],
    },
    {
      id: "cash-position",
      title: "Cash Position",
      // All users with any financial view permission
      requiredAnyPermission: [
        "view_current_cash_mini_card",
        "view_bank_accounts_card",
        "view_executive_cash_position_card",
        "view_30day_forecast_mini_card",
      ],
      items: [
        {
          id: "CP-01",
          label: "Cash position below healthy threshold",
          locked: true,
          lockedReason:
            "Required to ensure critical cash alerts are never missed",
        },
        {
          id: "CP-02",
          label: "Cash position recovered above threshold",
          locked: false,
        },
        {
          id: "CP-03",
          label: "Cash position below caution threshold",
          locked: true,
          lockedReason: "Required to ensure caution alerts are never missed",
        },
        {
          id: "CP-04",
          label: "Cash position negative",
          locked: true,
          lockedReason: "Required — negative cash is always a critical event",
        },
      ],
    },
  ];
}

// ─── Section component ────────────────────────────────────────────────────────

interface SectionProps {
  section: NotificationSection;
  enabledIds: Set<string>;
  onToggle: (id: string) => void;
}

const NotificationSectionBlock: React.FC<SectionProps> = ({
  section,
  enabledIds,
  onToggle,
}) => {
  const [open, setOpen] = useState(false);

  const toggleableItems = section.items.filter((i) => !i.locked);
  const allToggleableEnabled = toggleableItems.every((i) =>
    enabledIds.has(i.id),
  );

  const handleSelectAll = () => {
    toggleableItems.forEach((i) => {
      if (!enabledIds.has(i.id)) onToggle(i.id);
    });
  };

  const handleDeselectAll = () => {
    toggleableItems.forEach((i) => {
      if (enabledIds.has(i.id)) onToggle(i.id);
    });
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border rounded-lg">
        {/* Header */}
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/40 transition-colors rounded-lg"
          >
            <span className="font-medium text-sm text-gray-900">
              {section.title}
            </span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">
                {section.items.filter((i) => !i.locked && enabledIds.has(i.id))
                  .length + section.items.filter((i) => i.locked).length}
                /{section.items.length} on
              </span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-gray-400 transition-transform duration-200",
                  open && "rotate-180",
                )}
              />
            </div>
          </button>
        </CollapsibleTrigger>

        {/* Content */}
        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-1 border-t pt-3">
            {/* Select all / Deselect all — only if there are toggleable items */}
            {toggleableItems.length > 0 && (
              <div className="flex items-center gap-3 mb-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-teal-700 hover:text-teal-800 hover:bg-teal-50 px-2"
                  onClick={handleSelectAll}
                  disabled={allToggleableEnabled}
                >
                  Select all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-2"
                  onClick={handleDeselectAll}
                  disabled={toggleableItems.every((i) => !enabledIds.has(i.id))}
                >
                  Deselect all
                </Button>
              </div>
            )}

            {section.items.map((item) => (
              <div
                key={item.id}
                className="flex items-start justify-between gap-3 py-1.5"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "text-sm",
                        item.locked ? "text-gray-500" : "text-gray-800",
                      )}
                    >
                      {item.label}
                    </span>
                    {item.locked && (
                      <Lock className="h-3 w-3 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  {item.locked && item.lockedReason && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {item.lockedReason}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 mt-0.5">
                  {item.locked ? (
                    <Switch checked={true} disabled className="opacity-40" />
                  ) : (
                    <Switch
                      checked={enabledIds.has(item.id)}
                      onCheckedChange={() => onToggle(item.id)}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const NotificationsTab: React.FC = () => {
  const { hasAnyPermission, hasPermission } = usePermissions();
  const { getForecastTerm, getScenarioTerm, getActionsTerm } = useTerminology();

  const sections = buildNotificationSections({
    forecast: getForecastTerm,
    scenario: getScenarioTerm,
    actions: getActionsTerm,
  });

  // Build initial enabled set from non-locked items (all on by default).
  // Item ids are terminology-independent, so this only needs to run once.
  const [enabledIds, setEnabledIds] = useState<Set<string>>(
    () =>
      new Set<string>(
        sections.flatMap((s) =>
          s.items.filter((i) => !i.locked).map((i) => i.id),
        ),
      ),
  );

  const handleToggle = (id: string) => {
    setEnabledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Filter sections by permission
  const visibleSections = sections.filter((section) => {
    if (!section.requiredAnyPermission) return true;
    return section.requiredAnyPermission.some((p) => hasPermission(p as never));
  });

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-500 mb-4">
        Control which events appear in your notification feed. Items marked with
        a lock icon{" "}
        <Lock className="h-3 w-3 inline-block mx-0.5 text-gray-400" /> are
        always active and cannot be turned off.
      </div>

      {visibleSections.map((section) => (
        <NotificationSectionBlock
          key={section.id}
          section={section}
          enabledIds={enabledIds}
          onToggle={handleToggle}
        />
      ))}

      {visibleSections.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-8">
          No notification settings are available for your current account.
        </p>
      )}
    </div>
  );
};
